class RawFilmsTheme {
  constructor() {
    this.body = document.body;
    this.progress = document.querySelector('[data-scroll-progress] span');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.scrollFrame = null;
    this.cacheScrollTargets();
    this.init();
  }

  // Re-queried whenever a section is replaced in the theme editor, otherwise
  // these would keep pointing at detached nodes and the effects would go dead.
  cacheScrollTargets() {
    this.header = document.querySelector('[data-site-header]');
    this.hero = document.querySelector('[data-video-section]');
    this.heroMedia = this.hero?.querySelector('.hero-video__media-wrap');
    this.scrollRowSections = [...document.querySelectorAll('[data-scroll-rows]')];
  }

  init() {
    this.setupPageEntry();
    this.setupBackToTop(document);
    this.setupMenu(document);
    this.setupScrollEffects();
    this.setupReveal(document);
    this.setupMagneticButtons(document);
    this.setupProjectVideos(document);
    this.setupWorkMedia(document);
    this.setupMetricCounters(document);
    this.setupWorkFilters(document);
    this.setupQuantityControls(document);
    this.setupThemeEditor();
  }

  setupPageEntry() {
    requestAnimationFrame(() => requestAnimationFrame(() => this.body.classList.add('is-ready')));
    window.addEventListener('pageshow', () => this.body.classList.add('is-ready'));
  }

  setupBackToTop(root) {
    root.querySelectorAll('.footer-back-to-top a:not([data-back-to-top-ready])').forEach((link) => {
      link.dataset.backToTopReady = 'true';
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const start = window.scrollY;
        if (start <= 0) return;
        if (this.reducedMotion) {
          window.scrollTo(0, 0);
          return;
        }

        const duration = Math.min(1800, Math.max(900, start * 0.28));
        const startedAt = performance.now();
        const rootElement = document.documentElement;
        const previousScrollBehavior = rootElement.style.scrollBehavior;
        rootElement.style.scrollBehavior = 'auto';

        const animate = (now) => {
          const progress = Math.min((now - startedAt) / duration, 1);
          const eased = progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
          window.scrollTo(0, Math.round(start * (1 - eased)));
          if (progress < 1) requestAnimationFrame(animate);
          else rootElement.style.scrollBehavior = previousScrollBehavior;
        };

        requestAnimationFrame(animate);
      });
    });
  }

  setupMenu(root) {
    const menuToggle = root.querySelector('[data-menu-toggle]:not([data-menu-ready])');
    const drawer = root.querySelector('[data-mobile-drawer]');
    if (!menuToggle || !drawer) return;
    menuToggle.dataset.menuReady = 'true';
    this.menuToggle = menuToggle;
    this.drawer = drawer;
    this.body.classList.remove('menu-open');

    this.setMenu = (open) => {
      this.body.classList.toggle('menu-open', open);
      menuToggle.setAttribute('aria-expanded', String(open));
      drawer.setAttribute('aria-hidden', String(!open));
    };
    menuToggle.addEventListener('click', () => this.setMenu(!this.body.classList.contains('menu-open')));
    drawer.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => this.setMenu(false)));

    if (this.escapeBound) return;
    this.escapeBound = true;
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.body.classList.contains('menu-open')) {
        this.setMenu(false);
        this.menuToggle?.focus();
      }
    });
  }

  setupScrollEffects() {
    const update = () => {
      const scrollY = window.scrollY;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollable > 0 ? Math.min(scrollY / scrollable, 1) : 0;
      if (this.progress) this.progress.style.transform = `scaleX(${progress})`;
      if (this.header?.dataset.sticky === 'true') {
        const pinned = scrollY > 120;
        this.header.classList.toggle('is-scrolled', pinned);
        // Pinning removes the header from flow; the body class lets CSS give
        // the height back to <main> so the page does not jump.
        this.body.classList.toggle('header-pinned', pinned);
      }
      if (this.heroMedia && !this.reducedMotion && scrollY < window.innerHeight) {
        this.heroMedia.style.setProperty('--rf-hero-shift', `${Math.min(scrollY * 0.08, 42)}px`);
      }
      if (!this.reducedMotion) {
        this.scrollRowSections.forEach((section) => {
          const rect = section.getBoundingClientRect();
          const progress = Math.max(0, Math.min(1, (window.innerHeight - rect.top) / (window.innerHeight + rect.height)));
          const travel = Math.min(window.innerWidth * 0.24, 380);
          section.querySelectorAll('[data-scroll-row]').forEach((row) => {
            const direction = Number(row.dataset.direction) || 1;
            const shift = direction < 0 ? -progress * travel : -travel + progress * travel;
            row.querySelector('.partner-row__track')?.style.setProperty('--row-shift', `${shift}px`);
          });
        });
      }
      this.scrollFrame = null;
    };
    const requestUpdate = () => {
      if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
  }

  setupReveal(root) {
    const automatic = [
      '.section-heading:not(.reveal)',
      '.rich-text:not(.reveal)',
      '.product-info:not(.reveal)',
      '.article-content:not(.reveal)',
      '.cart-layout:not(.reveal)',
      '.simple-page__inner:not(.reveal)'
    ];
    root.querySelectorAll(automatic.join(',')).forEach((item) => item.classList.add('reveal'));
    const items = [...root.querySelectorAll('.reveal:not([data-motion-ready])')];
    if (!items.length) return;

    root.querySelectorAll('.grid,.form-grid,.value-list,.footer-main').forEach((group) => {
      [...group.children].forEach((item, index) => item.style.setProperty('--reveal-delay', `${Math.min(index, 6) * 70}ms`));
    });

    if (this.reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.motionReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -8% 0px' });

    items.forEach((item) => {
      item.dataset.motionReady = 'true';
      observer.observe(item);
    });
  }

  setupMagneticButtons(root) {
    if (this.reducedMotion || !window.matchMedia('(pointer:fine)').matches) return;
    root.querySelectorAll('.button:not([data-magnetic-ready])').forEach((button) => {
      button.dataset.magneticReady = 'true';
      button.addEventListener('pointermove', (event) => {
        const rect = button.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 9;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 7;
        button.style.setProperty('--magnetic-x', `${x}px`);
        button.style.setProperty('--magnetic-y', `${y}px`);
      });
      button.addEventListener('pointerleave', () => {
        button.style.setProperty('--magnetic-x', '0px');
        button.style.setProperty('--magnetic-y', '0px');
      });
    });
  }

  setupProjectVideos(root) {
    const videos = [...root.querySelectorAll('.project-card video:not([data-project-video-ready])')];
    if (!videos.length) return;
    videos.forEach((video) => {
      video.dataset.projectVideoReady = 'true';
      video.muted = true;
    });
    if (this.reducedMotion || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
    }, { threshold: 0.3, rootMargin: '80px 0px' });
    videos.forEach((video) => observer.observe(video));
  }

  setupWorkMedia(root) {
    root.querySelectorAll('[data-work-video]:not([data-work-video-ready])').forEach((wrapper) => {
      wrapper.dataset.workVideoReady = 'true';
      const video = wrapper.querySelector('video');
      const button = wrapper.querySelector('[data-work-video-toggle]');
      if (!video || !button) return;

      const showCoverFrame = () => {
        if (video.dataset.coverFrameReady === 'true') return;
        const requestedTime = Number(wrapper.dataset.coverTime) || 0;
        const latestFrame = Number.isFinite(video.duration) ? Math.max(0, video.duration - 0.1) : requestedTime;
        video.currentTime = Math.min(Math.max(0, requestedTime), latestFrame);
        video.dataset.coverFrameReady = 'true';
      };

      if (video.readyState >= 1) showCoverFrame();
      else video.addEventListener('loadedmetadata', showCoverFrame, { once: true });

      const syncState = () => {
        const playing = !video.paused && !video.ended;
        wrapper.classList.toggle('is-playing', playing);
        button.setAttribute('aria-label', `${playing ? 'Pause' : 'Play'} video`);
      };

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (video.paused) {
          document.querySelectorAll('[data-work-video] video').forEach((other) => {
            if (other !== video) other.pause();
          });
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      });
      video.addEventListener('play', syncState);
      video.addEventListener('pause', syncState);
      video.addEventListener('ended', syncState);
      syncState();
    });

    root.querySelectorAll('[data-work-gallery]:not([data-work-gallery-ready])').forEach((gallery) => {
      gallery.dataset.workGalleryReady = 'true';
      const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
      const buttons = [...gallery.querySelectorAll('[data-gallery-button]')];
      buttons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const selected = button.dataset.galleryButton;
          slides.forEach((slide) => { slide.hidden = slide.dataset.gallerySlide !== selected; });
          buttons.forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
          button.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
        });
      });
    });
  }

  setupMetricCounters(root) {
    const counters = [...root.querySelectorAll('[data-count-up]:not([data-count-ready])')];
    if (!counters.length || this.reducedMotion || !('IntersectionObserver' in window)) return;
    const metrics = new WeakMap();

    const parseMetric = (element) => {
      const raw = element.textContent.trim();
      const match = raw.match(/-?\d[\d,]*(?:\.\d+)?/);
      if (!match) return null;
      const numericText = match[0];
      const decimalPart = numericText.split('.')[1];
      return {
        raw,
        target: Number.parseFloat(numericText.replaceAll(',', '')),
        prefix: raw.slice(0, match.index),
        suffix: raw.slice(match.index + numericText.length),
        decimals: decimalPart ? decimalPart.length : 0,
        grouped: numericText.includes(',')
      };
    };

    const animate = (element, metric, delay) => {
      window.setTimeout(() => {
        const duration = Number(element.dataset.countDuration) || 1600;
        const start = performance.now();
        element.classList.add('is-counting');
        const frame = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 4);
          const current = metric.target * eased;
          let number = metric.decimals ? current.toFixed(metric.decimals) : String(Math.round(current));
          if (metric.grouped) {
            number = Number(number).toLocaleString('en-US', {
              minimumFractionDigits: metric.decimals,
              maximumFractionDigits: metric.decimals
            });
          }
          element.textContent = `${metric.prefix}${number}${metric.suffix}`;
          if (progress < 1) requestAnimationFrame(frame);
          else {
            element.textContent = metric.raw;
            element.classList.remove('is-counting');
            element.classList.add('is-counted');
          }
        };
        requestAnimationFrame(frame);
      }, delay);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const element = entry.target;
        const metric = metrics.get(element);
        if (metric && Number.isFinite(metric.target)) animate(element, metric, Number(element.dataset.countIndex) * 90);
        observer.unobserve(element);
      });
    }, { threshold: 0.45, rootMargin: '0px 0px -5% 0px' });

    counters.forEach((element, index) => {
      const metric = parseMetric(element);
      element.dataset.countReady = 'true';
      element.dataset.countIndex = String(index % 3);
      if (metric && Number.isFinite(metric.target)) {
        metrics.set(element, metric);
        const zero = metric.decimals ? (0).toFixed(metric.decimals) : '0';
        element.textContent = `${metric.prefix}${zero}${metric.suffix}`;
        observer.observe(element);
      }
    });
  }

  setupWorkFilters(root) {
    root.querySelectorAll('[data-work-portfolio]:not([data-work-ready])').forEach((portfolio) => {
      portfolio.dataset.workReady = 'true';
      const buttons = [...portfolio.querySelectorAll('[data-work-filter]')];
      const cards = [...portfolio.querySelectorAll('[data-work-card]')];
      const empty = portfolio.querySelector('[data-work-empty]');
      if (!buttons.length || !cards.length) return;

      buttons.forEach((button) => button.addEventListener('click', () => {
        const filter = button.dataset.workFilter;
        let visible = 0;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', String(active));
        });
        cards.forEach((card) => {
          const matches = filter === 'all' || card.dataset.category === filter;
          card.hidden = !matches;
          if (!matches) card.querySelector('video')?.pause();
          if (matches) visible += 1;
        });
        if (empty) empty.hidden = visible > 0;
      }));
    });
  }

  setupQuantityControls(root) {
    root.querySelectorAll('[data-quantity]:not([data-quantity-ready])').forEach((wrapper) => {
      wrapper.dataset.quantityReady = 'true';
      const input = wrapper.querySelector('input');
      wrapper.querySelectorAll('button').forEach((button) => button.addEventListener('click', () => {
        const step = button.dataset.action === 'increase' ? 1 : -1;
        input.value = Math.max(1, Number(input.value || 1) + step);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    });
  }

  setupThemeEditor() {
    document.addEventListener('shopify:section:load', (event) => {
      this.cacheScrollTargets();
      this.setupMenu(event.target);
      this.setupReveal(event.target);
      this.setupBackToTop(event.target);
      this.setupMagneticButtons(event.target);
      this.setupProjectVideos(event.target);
      this.setupWorkMedia(event.target);
      this.setupMetricCounters(event.target);
      this.setupWorkFilters(event.target);
      this.setupQuantityControls(event.target);
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.rawFilmsTheme = new RawFilmsTheme();
});
